import axiosInstance from '../api/axiosInstance';
import Cookies from 'js-cookie';

// Types
export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  username: string;
  role: string;
}

export interface Company {
  id: string;
  name: string;
  type: string;
  address: string;
  city: string;
  country: string;
  phone: string;
  website?: string;
  description?: string;
}

export interface AuthResponse {
  user: User;
  company: Company;
  accessToken: string;
  refreshToken: string;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  phoneNumber: string;
}

// Auth API functions
export const authAPI = {
  // Login
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await axiosInstance.post('/auth/login', credentials);
    return response.data;
  },

  // Register
  register: async (data: RegisterData): Promise<AuthResponse> => {
    const response = await axiosInstance.post('/auth/register', data);
    return response.data;
  },

  // Logout
  logout: async (): Promise<void> => {
    await axiosInstance.post('/auth/logout');
  },

  // Get current user
  getCurrentUser: async (): Promise<User> => {
    const response = await axiosInstance.get('/users/me');
    return response.data;
  },

  // Refresh token
  refreshToken: async (): Promise<{ accessToken: string; refreshToken: string }> => {
    const response = await axiosInstance.post('/auth/refresh');
    return response.data;
  },

  // Forgot password
  forgotPassword: async (email: string): Promise<void> => {
    await axiosInstance.post('/auth/forgot-password', { email });
  },

  // Reset password
  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    await axiosInstance.post('/auth/reset-password', { token, newPassword });
  },

  // Change password
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<void> => {
    await axiosInstance.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  },

  // Update profile
  updateProfile: async (userData: Partial<User>): Promise<User> => {
    const response = await axiosInstance.put('/auth/profile', userData);
    return response.data;
  },

  // Update company
  updateCompany: async (companyData: Partial<Company>): Promise<Company> => {
    const response = await axiosInstance.put('/auth/company', companyData);
    return response.data;
  },
};

// Utility functions
export const authUtils = {
  // Save auth data to cookies
  saveAuthData: (authData: AuthResponse) => {
    if (!authData) return;

    // Set cookies with 7-day expiration
    const expires = 7; // days
    Cookies.set('authToken', authData.accessToken, {
      expires,
      secure: true,
      sameSite: 'strict',
    });
    Cookies.set('refreshToken', authData.refreshToken, {
      expires,
      secure: true,
      sameSite: 'strict',
    });
    Cookies.set('user', JSON.stringify(authData.user), {
      expires,
      secure: true,
      sameSite: 'strict',
    });
    if (authData.company) {
      Cookies.set('company', JSON.stringify(authData.company), {
        expires,
        secure: true,
        sameSite: 'strict',
      });
    }
  },

  // Get auth data from cookies
  getAuthData: (): AuthResponse | null => {
    const token = Cookies.get('authToken');
    const refreshToken = Cookies.get('refreshToken');
    const userStr = Cookies.get('user');
    const companyStr = Cookies.get('company');

    if (!token || !refreshToken || !userStr) {
      return null;
    }

    try {
      const user = JSON.parse(userStr) as User;
      const company = companyStr ? JSON.parse(companyStr) : null;

      return {
        accessToken: token,
        refreshToken,
        user,
        company,
      };
    } catch (error) {
      console.error('Error parsing auth data:', error);
      return null;
    }
  },

  // Clear auth data
  clearAuthData: () => {
    Cookies.remove('authToken');
    Cookies.remove('refreshToken');
    Cookies.remove('user');
    Cookies.remove('company');
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = Cookies.get('authToken');
    return !!token;
  },
};
